import React from 'react';
import { 
  ShieldCheckIcon, 
  HeartIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PhoneIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const Insurance: React.FC = () => {
  const coverageTypes = [
    {
      title: 'Medical Emergency Coverage',
      icon: HeartIcon,
      color: 'bg-red-100 text-red-600',
      description: 'Comprehensive medical coverage for emergencies during your safari',
      coverage: [
        'Emergency medical treatment and hospitalization',
        'Medical evacuation and repatriation',
        'Emergency dental treatment',
        'Prescription medication coverage',
        'Pre-existing condition coverage (with restrictions)'
      ]
    },
    {
      title: 'Trip Cancellation & Interruption',
      icon: ShieldCheckIcon,
      color: 'bg-blue-100 text-blue-600',
      description: 'Protection against trip cancellations and interruptions',
      coverage: [
        'Trip cancellation due to covered reasons',
        'Trip interruption and early return',
        'Missed connection coverage',
        'Weather-related cancellations',
        'Political unrest and natural disasters'
      ]
    },
    {
      title: 'Baggage & Personal Effects',
      icon: DocumentTextIcon,
      color: 'bg-green-100 text-green-600',
      description: 'Coverage for lost, stolen, or damaged personal belongings',
      coverage: [
        'Lost or stolen luggage',
        'Delayed baggage expenses',
        'Personal effects coverage',
        'Camera and equipment protection',
        'Travel document replacement'
      ]
    }
  ];

  const recommendedProviders = [
    {
      name: 'World Nomads',
      description: 'Specialized travel insurance for adventure travelers',
      features: [
        'Adventure sports coverage',
        '24/7 emergency assistance',
        'Flexible coverage options',
        'Digital nomad friendly'
      ],
      website: 'worldnomads.com'
    },
    {
      name: 'Allianz Travel',
      description: 'Comprehensive travel insurance with global coverage',
      features: [
        'Global medical network',
        'Trip cancellation coverage',
        'Baggage protection',
        'Emergency assistance'
      ],
      website: 'allianztravel.com'
    },
    {
      name: 'Travel Guard',
      description: 'Reliable travel insurance with excellent customer service',
      features: [
        'Comprehensive coverage options',
        '24/7 customer service',
        'Easy claims process',
        'Family coverage options'
      ],
      website: 'travelguard.com'
    }
  ];

  const importantConsiderations = [
    {
      title: 'Pre-Existing Conditions',
      description: 'Declare all pre-existing medical conditions when purchasing insurance',
      details: [
        'Some conditions may be excluded from coverage',
        'Additional premiums may apply',
        'Medical clearance may be required',
        'Coverage may be limited for certain conditions'
      ]
    },
    {
      title: 'Adventure Activities',
      description: 'Ensure your policy covers safari and adventure activities',
      details: [
        'Game drives and wildlife viewing',
        'Walking safaris and hiking',
        'Hot air balloon rides',
        'Cultural activities and village visits'
      ]
    },
    {
      title: 'Geographic Coverage',
      description: 'Verify coverage for Kenya, Tanzania, and other destinations',
      details: [
        'Coverage in remote safari areas',
        'Medical facilities accessibility',
        'Evacuation capabilities',
        'Local emergency services'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Travel Insurance</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Protect your safari investment with comprehensive travel insurance coverage.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Insurance Image Banner */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Travel insurance and protection"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/70 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-3xl font-bold mb-2">Protect Your Safari Investment</h2>
              <p className="text-xl">Comprehensive travel insurance for peace of mind</p>
            </div>
          </div>
        </div>

        {/* Why Insurance is Important */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mb-16">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-4">Why Travel Insurance is Essential</h2>
              <p className="text-neutral-600 mb-4">
                Safari travel involves unique risks and challenges. Travel insurance provides peace of mind and 
                financial protection against unexpected events that could disrupt or cancel your safari adventure.
              </p>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                  Medical emergencies in remote locations can be expensive
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                  Trip cancellations due to illness or family emergencies
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                  Lost or damaged camera equipment and personal belongings
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                  Emergency evacuation from remote safari locations
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Coverage Types */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Types of Coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coverageTypes.map((coverage, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className={`w-12 h-12 ${coverage.color} rounded-lg flex items-center justify-center mb-4`}>
                  <coverage.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-3">{coverage.title}</h3>
                <p className="text-neutral-600 mb-4">{coverage.description}</p>
                <ul className="space-y-2">
                  {coverage.coverage.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-sm text-neutral-700">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Recommended Providers */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Recommended Insurance Providers</h2>
          <div className="space-y-6">
            {recommendedProviders.map((provider, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary-900 mb-2">{provider.name}</h3>
                    <p className="text-neutral-600 mb-4">{provider.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-primary-900 mb-2">Key Features:</h4>
                        <ul className="space-y-1">
                          {provider.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start text-sm text-neutral-700">
                              <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-center">
                        <a 
                          href={`https://${provider.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-primary-800 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                        >
                          Visit Website
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Considerations */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Important Considerations</h2>
          <div className="space-y-8">
            {importantConsiderations.map((consideration, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
                <h3 className="text-lg font-semibold text-primary-900 mb-3">{consideration.title}</h3>
                <p className="text-neutral-600 mb-4">{consideration.description}</p>
                <ul className="space-y-2">
                  {consideration.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start text-neutral-700">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <PhoneIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Emergency Assistance</h3>
              <p className="text-red-700 mb-2">
                In case of emergency during your safari, contact our 24/7 emergency line:
              </p>
              <div className="text-red-600 font-bold text-xl mb-2">+254 700 000 001</div>
              <p className="text-sm text-red-600">
                Our emergency team can assist with medical emergencies, evacuation, and insurance claims.
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-neutral-100 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">Important Disclaimer</h3>
          <p className="text-neutral-600 text-sm">
            Maasai SightTrails Safaris does not provide travel insurance directly. We strongly recommend 
            that all travelers purchase comprehensive travel insurance from reputable providers. 
            We are not responsible for any insurance claims, coverage decisions, or disputes between 
            travelers and insurance providers. Please review all insurance terms and conditions carefully 
            before purchasing coverage.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Insurance;
